/**
 * Fleet Management Page
 * Integrates the AssetMixOptimizer component for fleet optimization
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Alert,
  AlertTitle,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocalShipping,
  DirectionsCar,
  TwoWheeler,
  DirectionsWalk,
  TrendingUp,
  AttachMoney,
  Speed,
  Assessment,
  Add,
  Settings,
  Refresh,
  Download,
} from '@mui/icons-material';
import { AssetMixOptimizer } from '../components/optimization/AssetMixOptimizer';
import { AdvancedDataTable } from '../components/tables/AdvancedDataTable';
import { useVehicles } from '../hooks/useVehicles';
import { useProject } from '../contexts/ProjectContext';
import type { Vehicle } from '../types/vrp';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const FleetManagement: React.FC = () => {
  const { currentProject } = useProject();
  const { data: vehicles = [], isLoading } = useVehicles(currentProject?.id || 0);
  const [activeTab, setActiveTab] = useState(0);

  // Calculate fleet metrics
  const fleetMetrics = {
    totalVehicles: vehicles.length,
    byType: {
      truck: vehicles.filter(v => v.profile === 'driving' && v.capacity[0] > 5000).length,
      van: vehicles.filter(v => v.profile === 'driving' && v.capacity[0] <= 5000).length,
      bike: vehicles.filter(v => v.profile === 'cycling').length,
      foot: vehicles.filter(v => v.profile === 'foot').length,
    },
    totalCapacity: vehicles.reduce((sum, v) => sum + (v.capacity[0] || 0), 0),
    avgUtilization: 78, // Simulated
    maintenanceDue: 3, // Simulated
    activeRoutes: vehicles.filter(v => v.profile === 'driving').length,
  };

  // Vehicle table columns
  const vehicleColumns = [
    {
      id: 'description',
      label: 'Vehicle Name',
      dataKey: 'description' as keyof Vehicle,
      sortable: true,
      searchable: true,
    },
    {
      id: 'profile',
      label: 'Type',
      dataKey: 'profile' as keyof Vehicle,
      sortable: true,
      filterable: true,
      filterOptions: [
        { value: 'driving', label: 'Truck/Van' },
        { value: 'cycling', label: 'Bike' },
        { value: 'foot', label: 'Walking' },
      ],
      render: (value: string) => {
        const icons = {
          driving: <DirectionsCar />,
          cycling: <TwoWheeler />,
          foot: <DirectionsWalk />,
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icons[value as keyof typeof icons] || <LocalShipping />}
            <Typography variant="body2">{value}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'capacity',
      label: 'Capacity',
      dataKey: 'capacity' as keyof Vehicle,
      render: (value: number[]) => `${value[0]} kg`,
      sortable: true,
    },
    {
      id: 'max_travel_time',
      label: 'Max Travel Time',
      dataKey: 'max_travel_time' as keyof Vehicle,
      render: (value: number) => `${value} min`,
      sortable: true,
    },
    {
      id: 'cost_per_km',
      label: 'Cost per km',
      dataKey: (row: Vehicle) => row.costs?.per_km || 0,
      render: (value: number) => `$${value.toFixed(2)}`,
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      dataKey: () => 'Active', // Simulated status
      render: (value: string) => (
        <Chip
          label={value}
          size="small"
          color={value === 'Active' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
  ];

  const handleOptimizationComplete = (result: any) => {
    console.log('Optimization complete:', result);
    // Handle optimization results
  };

  const exportFleetData = () => {
    console.log('Exporting fleet data...');
    // Implementation for exporting fleet data
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Fleet Modeling
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Optimize your fleet composition and model vehicle assignments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            size="small"
          >
            Fleet Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportFleetData}
            size="small"
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => window.location.href = '/vehicles'}
            size="small"
          >
            Add Vehicle
          </Button>
        </Box>
      </Box>

      {/* Fleet Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Fleet Size
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {fleetMetrics.totalVehicles}
                  </Typography>
                </Box>
                <LocalShipping sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Routes
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {fleetMetrics.activeRoutes}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Avg Utilization
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {fleetMetrics.avgUtilization}%
                  </Typography>
                </Box>
                <Speed sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
              </Box>
              <LinearProgress
                variant="determinate"
                value={fleetMetrics.avgUtilization}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Maintenance Due
                  </Typography>
                  <Typography variant="h4" fontWeight={600} color="warning.main">
                    {fleetMetrics.maintenanceDue}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fleet Type Breakdown */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <DirectionsCar sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">{fleetMetrics.byType.truck}</Typography>
            <Typography variant="body2" color="text.secondary">Trucks</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <DirectionsCar sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h6">{fleetMetrics.byType.van}</Typography>
            <Typography variant="body2" color="text.secondary">Vans</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <TwoWheeler sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h6">{fleetMetrics.byType.bike}</Typography>
            <Typography variant="body2" color="text.secondary">Bikes</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <DirectionsWalk sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h6">{fleetMetrics.byType.foot}</Typography>
            <Typography variant="body2" color="text.secondary">Walking</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper elevation={2}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Fleet Overview" />
          <Tab label="Asset Mix Optimization" />
          <Tab label="Cost Analysis" />
          <Tab label="Maintenance Schedule" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {!currentProject ? (
            <Alert severity="info">
              <AlertTitle>No Project Selected</AlertTitle>
              Please select a project from the Projects page to manage fleet.
            </Alert>
          ) : isLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading fleet data...</Typography>
            </Box>
          ) : vehicles.length === 0 ? (
            <Alert severity="warning">
              <AlertTitle>No Vehicles Found</AlertTitle>
              Add vehicles to your fleet to get started.
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                sx={{ mt: 2 }}
                onClick={() => window.location.href = '/vehicles'}
              >
                Add First Vehicle
              </Button>
            </Alert>
          ) : (
            <Box sx={{ p: 3 }}>
              <AdvancedDataTable
                columns={vehicleColumns}
                data={vehicles}
                onRowClick={(vehicle) => console.log('Vehicle clicked:', vehicle)}
                enableBulkOperations
                bulkActions={[
                  { label: 'Assign Route', action: (selected) => console.log('Assign route:', selected) },
                  { label: 'Schedule Maintenance', action: (selected) => console.log('Schedule maintenance:', selected) },
                  { label: 'Update Status', action: (selected) => console.log('Update status:', selected) },
                ]}
                enableExport
                exportFilename="fleet-vehicles"
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {!currentProject ? (
            <Alert severity="info">
              <AlertTitle>No Project Selected</AlertTitle>
              Please select a project to run asset mix optimization.
            </Alert>
          ) : (
            <Box sx={{ p: 3 }}>
              <AssetMixOptimizer
                onOptimizationComplete={handleOptimizationComplete}
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              <AlertTitle>Cost Analysis</AlertTitle>
              Detailed fleet cost analysis and comparison features coming soon.
            </Alert>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              <AlertTitle>Maintenance Schedule</AlertTitle>
              Vehicle maintenance tracking and scheduling features coming soon.
            </Alert>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};